import { DialogComponent } from "@components/Dialog";
import { FormProvider } from "@components/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Box, Button } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { taxesValidationSchema } from "../schema";
import { useTaxApi } from "../services/useTaxApi";
import { ITax } from "../types";
import { TaxesForm } from "./TaxesForm";
import { TaxesModalSkeleton } from "./TaxesModalSkeleton";

interface ITaxesModalProps {
  open: boolean;
  handleCloseModal: () => void;
  taxId?: string;
  onSuccess?: () => void;
  translate: (key: string) => string;
  selectedComplianceStandard?: string | null;
  isWithHoldingTaxVisible?: boolean;
  hasItemTaxFromManualForm?: boolean;
}

export const TaxesModal = ({
  open,
  handleCloseModal,
  taxId,
  onSuccess,
  translate,
  selectedComplianceStandard,
  isWithHoldingTaxVisible = true,
  hasItemTaxFromManualForm = false,
}: ITaxesModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const defaultTaxValues: ITax = {
    code: "",
    exemptCode: "",
    description: "",
    taxType: "",
    complianceStandard: null,
    rate: 0,
    isWithholdingTax: false,
    isActive: true,
  };

  const validationSchema = useMemo(() => {
    return taxesValidationSchema(translate);
  }, [translate]);

  const methods = useForm<ITax>({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultTaxValues,
    mode: "onSubmit",
    shouldUnregister: true,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const { getTaxById, updateTax, createTax } = useTaxApi();

  const fetchTaxData = async (taxId: string) => {
    setIsLoading(true);
    try {
      const taxData = await getTaxById(taxId);
      if (taxData) {
        reset(taxData);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching tax data:", error);
      enqueueSnackbar(translate("error_fetching_tax_data"), {
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && taxId) {
      fetchTaxData(taxId);
    } else {
      reset({
        ...defaultTaxValues,
        isWithholdingTax: hasItemTaxFromManualForm ? true : false,
      });
    }
  }, [open, taxId]);

  const handleSaveTax = async (data: ITax) => {
    try {
      const payload = {
        ...data,
        complianceStandard: selectedComplianceStandard ?? null,
      };
      let response;
      if (taxId) {
        response = await updateTax({ ...payload, id: taxId });
        if (response?.status === 200) {
          enqueueSnackbar(translate("tax_updated_success"), {
            variant: "success",
          });
        }
      } else {
        response = await createTax(payload);
        if (response?.status === 200) {
          enqueueSnackbar(translate("tax_created_success"), {
            variant: "success",
          });
        }
      }
      if (response?.status === 200) {
        onSuccess?.();
        handleCloseModal();
        reset(defaultTaxValues);
      }
    } catch (error) {
      console.error("Error saving tax:", error);
      enqueueSnackbar(translate("something_went_wrong"), { variant: "error" });
    }
  };

  return (
    <DialogComponent
      isShowModal={open}
      showModalMethod={handleCloseModal}
      fullWidth
      maxWidth={"md"}
      contentSx={{ p: 0 }}
      dialogTitle={taxId ? translate("edit_tax") : translate("add_tax")}
      dialogContent={
        isLoading ? (
          <Box sx={{ p: 3 }}>
            <TaxesModalSkeleton />
          </Box>
        ) : (
          <FormProvider
            methods={methods}
            onSubmit={handleSubmit(handleSaveTax)}
          >
            <Box sx={{ p: 3 }}>
              <TaxesForm
                {...{
                  taxId,
                  isSubmitting,
                  translate,
                  isWithHoldingTaxVisible,
                  hasItemTaxFromManualForm,
                }}
              />
            </Box>
          </FormProvider>
        )
      }
      dialogActions={
        isLoading ? undefined : (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              size="small"
              disabled={isSubmitting}
            >
              {translate(`cancel`)}
            </Button>

            <LoadingButton
              variant="contained"
              onClick={handleSubmit(handleSaveTax)}
              loading={isSubmitting}
              size="small"
            >
              {taxId ? translate("update") : translate(`save`)}
            </LoadingButton>
          </Box>
        )
      }
    />
  );
};
